import { yupResolver } from '@hookform/resolvers/yup';
import {
  PermissionGroupModel,
  RoleModel,
  RoleMutationInput,
  useGetPermissionGroups,
} from 'api-hooks/role';
import { Spinner } from 'baseui/spinner';
import formSetErrors from 'common/helpers/form-setError';
import { themedUseStyletron } from 'common/styles/styletron.theme';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, Input } from 'components/elements';
import ErrorViewComponent from 'components/elements/error-view-component';
import FetchWrapperComponent from 'components/elements/fetch-wrapper-component';
import Text from 'components/elements/text';
import FormHeader from 'components/widgets/form-header';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import {
  CardContainer,
  CardContent,
  DetailContainer,
  FullContainer,
  HalfContainer,
  SectionContainer,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { RoleFormSchema, RoleFormType } from './form-type';
import PermissionGroupSelect from './permission-group-select';
import RoleSelectInput from './select-input';

interface Props {
  role?: RoleModel;
  onSubmit: (
    input: RoleMutationInput,
    form: ReturnType<typeof useForm<RoleFormType>>,
  ) => Promise<RoleModel | undefined>;
}

interface RoleRoleProps extends Props {
  permissionGroups: PermissionGroupModel[];
}

function RoleRoleForm(props: RoleRoleProps) {
  const { t } = useTranslation();
  const { role, permissionGroups } = props;
  const [css] = themedUseStyletron();
  const { asPath } = useRouter();

  const stylesCss = React.useMemo(
    () => ({
      content: css({
        width: '47%',
      }),
      saveButton: css({
        maxWidth: '200px',
        marginTop: '32px !important',
      }),
      mb16: css({
        marginBottom: '16px',
      }),
      title: css({
        fontWeight: '600',
        marginBottom: '8px',
      }),
      box: css({
        // border: '1px solid #bababa',
        padding: '16px 4px',
        margin: '0 2px',
        borderRadius: '8px',
        marginLeft: 'inherit !important',
        marginRight: 'inherit !important',
        marginTop: '16px !important',
        display: 'flex',
        flex: 1,
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
      }),
      line: css({
        width: '100%',
        borderBottom: '2px solid #bababa',
      }),
      permissionTitle: css({
        fontSize: '13px',
        fontWeight: 600,
      }),
      permission: css({
        margin: '0 0 24px 0',
      }),
      permissionContainer: css({
        width: '100%',
        flexWrap: 'wrap',
      }),
      container: css({
        display: 'flex',
        flexDirection: 'column',
      }),
    }),
    [css],
  );

  const initialValues = React.useMemo<RoleFormType>(() => {
    return {
      name: role?.name || '',
      permissions: role?.permissionGroups || [],
      copier: '',
    };
  }, [role]);

  const methods = useForm({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(RoleFormSchema()),
  });

  const { setValue } = methods;

  const onSubmit = React.useCallback(
    async (values: RoleFormType) => {
      try {
        await props.onSubmit(values, methods);
      } catch (err) {
        if (err.errors) {
          formSetErrors(err.errors, methods.setError);
        }
        err?.message && notification.error({ message: err.message });
      }
    },
    [methods, props],
  );

  const onCopierRole = (value: RoleModel) => {
    setValue('permissions', value.permissionGroups);
  };

  const permission = useGetCrudPermission('roles');
  const { onDelete, isLoading: isDeleteLoading } = useDestroyAction(
    'roles',
    role?.id,
  );

  const customViewName = role && [t('common:role'), role?.name].join(' - ');

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!role}
      customViewName={customViewName}
      keyName={asPath}
    >
      <FormHeader
        onDelete={onDelete}
        data={role}
        isDeleteLoading={isDeleteLoading}
        noDelete={!permission.delete}
        noEdit={!permission.update}
        title={
          role
            ? customViewName ?? t('common:role')
            : t('common:add_extra', { extra: t('common:role') })
        }
      />
      <CardContent>
        <SectionContainer>
          <HalfContainer>
            <Input
              name="name"
              type="text"
              label={t('common:role_name')}
              placeholder={t('common:role_name')}
              required
            />
            {!role && (
              <RoleSelectInput
                label={t('role:duplicate_from')}
                name="copier"
                onAfterDetailChange={onCopierRole}
                clearable={false}
                noMargin={false}
              />
            )}
          </HalfContainer>
        </SectionContainer>
        <div className={stylesCss.container}>
          <Text
            variant="body2Regular"
            $style={{
              opacity: 0.5,
            }}
          >
            {t('common:permissions')}
          </Text>
          <Separator gap={4} />
          <CardContainer>
            <FullContainer>
              <SectionContainer
                style={{
                  flexWrap: 'wrap',
                  gap: 32,
                }}
              >
                {permissionGroups.map((pg) => {
                  const categories = pg.categories;
                  return (
                    <HalfContainer key={pg.group}>
                      <Text variant="body2Semibold">{pg.group}</Text>
                      <Separator gap={16} />
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                        }}
                      >
                        {categories.map((category) => {
                          return (
                            <PermissionGroupSelect
                              data={category}
                              key={category.category}
                            />
                          );
                        })}
                      </div>
                    </HalfContainer>
                  );
                })}
              </SectionContainer>
            </FullContainer>
          </CardContainer>
        </div>
      </CardContent>
    </Form>
  );
}

export default function RoleForm(props: Props) {
  const { role } = props;
  const { data, refetch, isLoading, isFetching, error } =
    useGetPermissionGroups();

  return (
    <>
      <FetchWrapperComponent
        onRetry={refetch}
        loadingComponent={
          <DetailContainer>
            <Spinner $color="black" />
          </DetailContainer>
        }
        error={error}
        errorComponent={
          <DetailContainer>
            <ErrorViewComponent refetch={refetch} />
          </DetailContainer>
        }
        isLoading={isLoading || isFetching}
        component={
          <RoleRoleForm
            role={role}
            onSubmit={props.onSubmit}
            permissionGroups={data?.data || []}
          />
        }
      />
    </>
  );
}
